import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PayrollAuthModal from '../components/PayrollAuthModal';
import { usePayrollAuthContext } from '../context/PayrollAuthContext';
import { usePayrollAuth } from './usePayrollAuth';
import type { ScheduleActionSectionKey } from '../lib/lockedSections';

/**
 * 스케줄 동작(생성·수정·삭제) 앞에 거는 2차 비밀번호 검사.
 * 설계: rappo docs/SCHEDULE_ACTION_LOCK_DESIGN.md §6-5
 *
 * ─────────────────────────────────────────────────────────────
 * 선물 잠금(useGiftSectionLock)과 따로 둔 이유
 * ─────────────────────────────────────────────────────────────
 * 선물 쪽 방어(열 때·보낼 때 두 번 검사, 창을 갈아 끼우는 방식)는 이미 검증을 거쳤다.
 * 일반화하며 손대면 그 방어가 흔들린다 — 그래서 스케줄용을 새로 만든다.
 *
 * ─────────────────────────────────────────────────────────────
 * 쓰는 법
 * ─────────────────────────────────────────────────────────────
 *   const lock = useScheduleActionLock('schedule.delete');
 *   onClick={() => lock.run(() => 삭제_확인창_열기())}     // 누를 때
 *   if (lock.requiresCheck() && !(await lock.ensure())) return;  // 저장 직전 재검사(30분 만료 구멍)
 *   {lock.gate}                                            // 컴포넌트 어딘가에 그대로 둔다
 *
 *  · 판정 전(unknown)이면 「확인하는 중」을 띄우고 **판정이 끝나면 다시 본다** — 열림이면 비밀번호 없이 진행.
 *  · 잠김·미인증이면 기존 2차 비밀번호 카드 → 성공하면 이어서 실행, 취소하면 **아무것도 하지 않는다**
 *    (화면 이동 없음).
 *  · 인증 카드는 모든 스케줄·그룹 창과 전역 확인창 위(z 10150)에 body 포털로 뜨고, 떠 있는 동안 아래로
 *    가는 키 입력·포커스를 막는다.
 *
 * 정직하게: 이건 화면 방어다. 서버는 이 잠금을 강제하지 않는다(선물 잠금과 같은 정책).
 * 인증 30분은 섹션 구분 없이 공유된다(기존 정책 — 정산에서 인증하면 일정 쓰기도 열린다).
 */

const ACTION_LABEL: Record<ScheduleActionSectionKey, string> = {
    'schedule.create': '수업 생성',
    'schedule.edit': '수업 수정',
    'schedule.delete': '수업 삭제',
};

/** 인증 카드 층위 — 스케줄 상세(2000)·그룹 팝오버(3000)·전역 확인창·시트보다 위 */
export const SCHEDULE_ACTION_LOCK_Z = 10150;

type Phase = 'resolving' | 'auth' | null;

export type ScheduleActionLock = {
    /** 지금 누르면 확인(판정 대기·비밀번호)이 필요한가 — 렌더 시점 값 */
    needsCheck: boolean;
    /** 동작을 잠금 판정 뒤에 실행한다. 취소하면 실행하지 않는다 */
    run: (action: () => void) => void;
    /**
     * 지금 확인(판정 대기·비밀번호)이 필요한가 — **부를 때** 판정한다.
     * 열려 있으면 기다리지 않고 그대로 진행해야 기존의 동기 중복 제출 잠금이 흔들리지 않는다:
     *   `if (lock.requiresCheck() && !(await lock.ensure())) return;`
     */
    requiresCheck: () => boolean;
    /** 저장 직전 재검사. 통과하면 true, 취소하면 false */
    ensure: () => Promise<boolean>;
    /** 확인·인증 카드가 떠 있는가 */
    isAsking: boolean;
    /** 컴포넌트 어딘가에 그대로 둔다(body 포털로 뜬다) */
    gate: ReactNode;
};

export const useScheduleActionLock = (sectionKey: ScheduleActionSectionKey): ScheduleActionLock => {
    const { isSectionLocked, isSectionUnlocked, unlockSection, setLastSection, getLockSupport } =
        usePayrollAuthContext();
    const { isLoading, isPasswordSet, verifyPassword, setPassword } = usePayrollAuth();
    const [phase, setPhase] = useState<Phase>(null);
    /** 결과를 기다리는 호출들 — 연타·저장 재검사가 겹쳐도 카드는 하나, 결과는 모두에게 */
    const waitersRef = useRef<Array<(ok: boolean) => void>>([]);
    const overlayRef = useRef<HTMLDivElement>(null);

    /** 그 순간의 판정(isSectionUnlocked 는 부를 때마다 30분 만료를 다시 본다) */
    const needsUnlockNow = useCallback(
        () => isSectionLocked(sectionKey) && !isSectionUnlocked(sectionKey),
        [isSectionLocked, isSectionUnlocked, sectionKey],
    );
    const isResolving = useCallback(() => getLockSupport() === 'unknown', [getLockSupport]);

    /*
     * 판정은 **부르는 순간의 최신 상태**로 한다. 확인창(전역 알림)이 run 을 콜백에 담아 두면
     * 렌더 때의 함수가 남는다 — 그사이 다른 기기에서 잠금이 걸려도 예전 판정으로 통과하면 안 된다.
     */
    const needsUnlockRef = useRef(needsUnlockNow);
    needsUnlockRef.current = needsUnlockNow;
    const isResolvingRef = useRef(isResolving);
    isResolvingRef.current = isResolving;

    const finish = useCallback((ok: boolean) => {
        const waiters = waitersRef.current;
        waitersRef.current = [];
        setPhase(null);
        waiters.forEach((resolve) => resolve(ok));
    }, []);

    const ensure = useCallback((): Promise<boolean> => {
        if (!isResolvingRef.current() && !needsUnlockRef.current()) return Promise.resolve(true);
        return new Promise<boolean>((resolve) => {
            waitersRef.current.push(resolve);
            setPhase(isResolvingRef.current() ? 'resolving' : 'auth');
        });
    }, []);

    const run = useCallback(
        (action: () => void) => {
            // 열려 있으면 그 자리에서 바로 — 클릭 처리 흐름을 바꾸지 않는다
            if (!isResolvingRef.current() && !needsUnlockRef.current()) {
                action();
                return;
            }
            void ensure().then((ok) => {
                if (ok) action();
            });
        },
        [ensure],
    );

    /*
     * 판정이 끝나면 다시 본다.
     *  · 확인 중 → 판정 끝: 열림이면 비밀번호 없이 통과, 잠김이면 카드.
     *  · 카드가 떠 있는 동안 다른 곳에서 인증했거나 잠금이 풀렸으면 통과.
     */
    useEffect(() => {
        if (phase === 'resolving') {
            if (isResolving()) return;
            if (!needsUnlockNow()) finish(true);
            else setPhase('auth');
            return;
        }
        if (phase === 'auth' && !isResolving() && !needsUnlockNow()) finish(true);
    }, [phase, isResolving, needsUnlockNow, finish]);

    /* 화면이 사라지면 기다리던 호출은 취소로 끝낸다(매달린 약속을 남기지 않는다) */
    useEffect(
        () => () => {
            const waiters = waitersRef.current;
            waitersRef.current = [];
            waiters.forEach((resolve) => resolve(false));
        },
        [],
    );

    /*
     * 카드가 떠 있는 동안 아래 화면으로 입력이 새지 않게 한다.
     *  · 키 입력은 카드 밖(문서·아래 모달의 ESC 처리 등)으로 올라가지 않는다.
     *  · 포커스가 카드 밖으로 나가면 카드 안으로 되돌린다.
     */
    useEffect(() => {
        if (!phase) return undefined;
        const onFocusIn = (event: FocusEvent) => {
            const overlay = overlayRef.current;
            if (!overlay || overlay.contains(event.target as Node)) return;
            const target = overlay.querySelector<HTMLElement>('input, button, [tabindex]');
            (target ?? overlay).focus();
        };
        document.addEventListener('focusin', onFocusIn, true);
        return () => document.removeEventListener('focusin', onFocusIn, true);
    }, [phase]);

    const submit = useCallback(
        async (password: string) => {
            try {
                /*
                 * 설정·확인은 같은 요청이다. 서버가 답으로 구분한다: 200 = 맞음(인증), 201 = 새로 만듦(인증 아님).
                 * 설정 여부를 화면 상태로 미리 가르면 안 된다 — 첫 조회가 실패해 「모름」이면 '설정' 단계로 보이는데,
                 * 그때 맞는 비밀번호를 넣어 200 이 와도 버리게 된다.
                 */
                const result = await (isPasswordSet ? verifyPassword : setPassword)(password);
                // 서버가 200 으로 답했을 때만 연다. resolve 만으로 열면 틀린 비밀번호도 통과한다.
                if (result?.status !== 200) return;
                unlockSection(sectionKey);
                setLastSection(sectionKey);
                finish(true);
            } catch {
                // usePayrollAuth 가 상태별 안내를 이미 띄운다. 카드는 열어 둔다.
            }
        },
        [finish, isPasswordSet, sectionKey, setLastSection, setPassword, unlockSection, verifyPassword],
    );

    const label = ACTION_LABEL[sectionKey];
    const gate =
        phase && typeof document !== 'undefined'
            ? createPortal(
                  <div
                      ref={overlayRef}
                      role="dialog"
                      aria-modal="true"
                      aria-label={`${label} 잠금 확인`}
                      tabIndex={-1}
                      className="fixed inset-0 overflow-auto bg-[#F4F5F7]"
                      style={{ zIndex: SCHEDULE_ACTION_LOCK_Z, pointerEvents: 'auto' }}
                      onKeyDown={(event) => event.stopPropagation()}
                      onKeyUp={(event) => event.stopPropagation()}
                      /* 포털이어도 React 이벤트는 컴포넌트 트리를 타고 올라간다 — 아래 모달의 닫기 처리로 새지 않게 */
                      onPointerDown={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                  >
                      <div className="pointer-events-none absolute left-0 right-0 top-[92px] text-center">
                          <p className="text-[14px] font-bold leading-[20px] text-[#3D4244]">
                              {label}은 잠긴 기능이에요
                          </p>
                          <p className="mt-[4px] text-[13px] leading-[19px] text-[#8E9AA0]">
                              탭 잠금 설정에서 잠가 둔 동작이라 2차 비밀번호가 필요해요
                          </p>
                      </div>
                      {phase === 'resolving' || isLoading ? (
                          <div className="flex h-full flex-col items-center justify-center gap-[12px]">
                              <span className="text-[14px] text-[#8E9AA0]">잠금 설정을 확인하는 중이에요…</span>
                              <button
                                  type="button"
                                  className="text-[13px] text-[#6B7684] underline"
                                  onClick={() => finish(false)}
                              >
                                  취소
                              </button>
                          </div>
                      ) : (
                          <PayrollAuthModal
                              step={isPasswordSet ? 'confirm' : 'set'}
                              onSubmit={submit}
                              /* 취소는 화면 이동이 아니라 **아무것도 하지 않기**다 */
                              onCancel={() => finish(false)}
                          />
                      )}
                  </div>,
                  document.body,
              )
            : null;

    return {
        needsCheck: isResolving() || needsUnlockNow(),
        run,
        requiresCheck: () => isResolvingRef.current() || needsUnlockRef.current(),
        ensure,
        isAsking: phase !== null,
        gate,
    };
};
